import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuthStore } from '../../state/auth.store';

/**
 * The page's single most important action, factored out so EVERY primary CTA is identical: same styling +
 * hover micro-interaction (scale + glow + a sheen sweep), same `data-testid` the e2e agent keys off. The
 * hover sheen is a pure CSS transform/opacity animation (GPU-friendly) and is harmless under reduced motion
 * (it only plays on deliberate hover, never autonomously).
 *
 * Authed-aware: a logged-OUT visitor gets "Sign up" → /register (create an account — the login wall); a
 * RETURNING authed device gets "Enter lobby" → /lobby (one tap back to the hub). An explicit `children`
 * label always wins over the auto label.
 *
 * IDLE PULSE (P1): when `pulse` is set (the hero + closing CTAs), a soft amber glow ring breathes slowly
 * behind the button to pull the eye to the one action we most want clicked. It animates opacity/scale only
 * (GPU-cheap) and is fully gated behind `useReducedMotion` — under reduced motion the ring holds a calm
 * static glow with no animation.
 */
export function CreateRoomButton({
  size = 'lg',
  className = '',
  children,
  pulse = false,
}: {
  size?: 'lg' | 'md';
  className?: string;
  children?: React.ReactNode;
  /** Add a slow, attention-pulling glow pulse behind the button (the hero + final CTAs). */
  pulse?: boolean;
}): JSX.Element {
  const reduce = useReducedMotion();
  const authed = useAuthStore((s) => s.tokens !== null);
  // Authed → the lobby (the hub where you browse, create, and enter rooms); new visitor → sign up.
  const to = authed ? '/lobby' : '/register';
  const label = children ?? (authed ? 'Enter lobby' : 'Sign up');
  const sizing =
    size === 'lg'
      ? 'px-8 py-4 text-lg'
      : 'px-5 py-2.5 text-base';
  return (
    <span className="relative inline-flex">
      {/* Idle pulse glow — a soft amber halo breathing behind the button (reduced-motion → calm + static). */}
      {pulse && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-2 rounded-full bg-amber-400/40 blur-xl"
          initial={false}
          animate={reduce ? { opacity: 0.4, scale: 1 } : { opacity: [0.3, 0.65, 0.3], scale: [0.96, 1.06, 0.96] }}
          transition={reduce ? undefined : { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <Link
        to={to}
        data-testid="cta-create-room"
        className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-amber-400 font-bold text-slate-900 shadow-[0_8px_40px_rgba(255,210,63,0.4)] outline-none transition-transform duration-200 hover:scale-[1.04] focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1020] ${sizing} ${className}`}
      >
        {/* Sheen sweep on hover — a soft diagonal highlight that slides across the button. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
        />
        <span className="relative">{label}</span>
        <span aria-hidden="true" className="relative transition-transform duration-200 group-hover:translate-x-0.5">→</span>
      </Link>
    </span>
  );
}
